import { useEffect, useState } from 'react'
import { colors, spacing, fontSize, radius } from '../styles/theme'
import { useIsMobile } from '../hooks/useMediaQuery'

const BASE_URL = 'https://api.docmorphia.com/api/v1'
const DOCMOSIS_BASE_URL = 'https://api.docmorphia.com/docmosis/api'
const CARBONE_BASE_URL = 'https://api.docmorphia.com/carbone'

// ── Shared primitives ────────────────────────────────────────────────────────

function Code({ children }: { children: string }) {
  return (
    <code style={{
      fontFamily: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
      fontSize: '13px',
      background: '#0A1413',
      color: '#e2e8f0',
      padding: '2px 6px',
      borderRadius: radius.sm,
    }}>{children}</code>
  )
}

function CodeBlock({ children, language = '' }: { children: string; language?: string }) {
  const [copied, setCopied] = useState(false)

  const copy = () => {
    navigator.clipboard.writeText(children.trim())
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div style={{ position: 'relative', margin: `${spacing.md} 0` }}>
      {language && (
        <div style={{
          fontSize: fontSize.xs,
          color: '#64748b',
          background: '#0A1413',
          padding: '4px 14px',
          borderRadius: `${radius.md} ${radius.md} 0 0`,
          fontFamily: 'ui-monospace, monospace',
          borderBottom: '1px solid #334155',
        }}>{language}</div>
      )}
      <pre style={{
        margin: 0,
        padding: `${spacing.md} 48px ${spacing.md} ${spacing.md}`,
        background: '#0A1413',
        color: '#e2e8f0',
        borderRadius: language ? `0 0 ${radius.md} ${radius.md}` : radius.md,
        fontSize: '13px',
        fontFamily: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
        overflowX: 'auto',
        lineHeight: 1.6,
        whiteSpace: 'pre',
      }}>
        <code>{children.trim()}</code>
      </pre>
      <button
        onClick={copy}
        style={{
          position: 'absolute',
          top: language ? '30px' : '8px',
          right: '8px',
          padding: '3px 8px',
          background: copied ? colors.success : '#334155',
          color: '#e2e8f0',
          border: 'none',
          borderRadius: radius.sm,
          fontSize: fontSize.xs,
          cursor: 'pointer',
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginBottom: spacing.xxl }}>
      <h2 style={{
        fontSize: fontSize.xl,
        fontWeight: 700,
        color: colors.text,
        margin: `0 0 ${spacing.md}`,
        paddingBottom: spacing.sm,
        borderBottom: `2px solid ${colors.border}`,
      }}>{title}</h2>
      {children}
    </section>
  )
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} style={{ marginBottom: spacing.lg }}>
      <h3 style={{
        fontSize: fontSize.lg,
        fontWeight: 600,
        color: colors.text,
        margin: `${spacing.lg} 0 ${spacing.sm}`,
      }}>{title}</h3>
      {children}
    </div>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return <p style={{ color: colors.textSecondary, lineHeight: 1.7, margin: `0 0 ${spacing.md}` }}>{children}</p>
}

function Callout({ type, children }: { type: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const bg = type === 'warning' ? colors.warningLight : colors.primaryLight
  const border = type === 'warning' ? colors.warning : colors.primary
  const label = type === 'warning' ? 'Warning' : type === 'tip' ? 'Tip' : 'Note'
  return (
    <div style={{
      border: `1px solid ${border}`,
      borderLeft: `4px solid ${border}`,
      background: bg,
      borderRadius: radius.md,
      padding: `${spacing.sm} ${spacing.md}`,
      margin: `${spacing.md} 0`,
      fontSize: fontSize.base,
      color: colors.text,
      lineHeight: 1.6,
    }}>
      <strong>{label}: </strong>{children}
    </div>
  )
}

function EndpointCard({ method, path, description, request, response }: {
  method: string
  path: string
  description: string
  request: string
  response: string
}) {
  return (
    <div style={{
      border: `1px solid ${colors.border}`,
      borderRadius: radius.md,
      overflow: 'hidden',
      marginBottom: spacing.lg,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        padding: `${spacing.sm} ${spacing.md}`,
        background: colors.background,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <span style={{
          padding: '2px 8px',
          background: '#10b981',
          color: '#fff',
          borderRadius: radius.sm,
          fontSize: fontSize.xs,
          fontWeight: 700,
          fontFamily: 'ui-monospace, monospace',
        }}>{method}</span>
        <code style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: fontSize.base,
          color: colors.text,
          fontWeight: 600,
        }}>{BASE_URL}{path}</code>
      </div>
      <div style={{ padding: spacing.md }}>
        <p style={{ margin: `0 0 ${spacing.md}`, color: colors.textSecondary, lineHeight: 1.6 }}>{description}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
              Request Body
            </div>
            <pre style={{
              margin: 0, padding: spacing.sm,
              background: '#0A1413', color: '#e2e8f0',
              borderRadius: radius.sm, fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              overflowX: 'auto', lineHeight: 1.5,
            }}><code>{request}</code></pre>
          </div>
          <div>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
              Response
            </div>
            <pre style={{
              margin: 0, padding: spacing.sm,
              background: '#0A1413', color: '#e2e8f0',
              borderRadius: radius.sm, fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              overflowX: 'auto', lineHeight: 1.5,
            }}><code>{response}</code></pre>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Table of contents ────────────────────────────────────────────────────────

const TOC = [
  { id: 'overview',     label: 'Overview' },
  { id: 'auth',         label: 'Authentication' },
  { id: 'live-test',    label: '  Live and Test keys' },
  { id: 'workflow',     label: 'Core Workflow' },
  { id: 'endpoints',    label: 'API Reference' },
  { id: 'render',       label: '  Render to PDF' },
  { id: 'list',         label: '  List Templates' },
  { id: 'variables',    label: '  Get Variables' },
  { id: 'embed-token',  label: '  Embed Token' },
  { id: 'iframe',       label: 'iframe Embedding' },
  { id: 'mcp',          label: 'MCP Server' },
  { id: 'docmosis',     label: 'Migrating from Docmosis' },
  { id: 'carbone',      label: 'Migrating from Carbone' },
  { id: 'errors',       label: 'Error Handling' },
]

// ── Page ─────────────────────────────────────────────────────────────────────

export default function ApiDocsPage() {
  const isMobile = useIsMobile()
  const [activeSection, setActiveSection] = useState('overview')
  const [tocOpen, setTocOpen] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) setActiveSection(e.target.id)
        }
      },
      { rootMargin: '-20% 0% -70% 0%' }
    )
    document.querySelectorAll('section[id], div[id]').forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return (
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '220px 1fr',
        gap: spacing.xl,
        padding: isMobile ? `${spacing.lg} ${spacing.md}` : `${spacing.xl} ${spacing.xl}`,
        alignItems: 'start',
      }}>
        {/* Sidebar TOC — desktop only */}
        {!isMobile && (
          <nav style={{
            position: 'sticky',
            top: 72,
            padding: spacing.md,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
          }}>
            <div style={{ fontSize: fontSize.xs, fontWeight: 700, color: colors.textMuted, marginBottom: spacing.sm, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Contents
            </div>
            {TOC.map(item => (
              <a
                key={item.id}
                href={`#${item.id}`}
                style={{
                  display: 'block',
                  padding: `4px ${item.label.startsWith('  ') ? spacing.lg : spacing.sm}`,
                  fontSize: fontSize.sm,
                  color: activeSection === item.id ? colors.primary : colors.textSecondary,
                  textDecoration: 'none',
                  borderRadius: radius.sm,
                  background: activeSection === item.id ? colors.primaryLight : 'transparent',
                  fontWeight: activeSection === item.id ? 600 : 400,
                  marginBottom: 2,
                }}
              >
                {item.label.trim()}
              </a>
            ))}
          </nav>
        )}

        {/* Content */}
        <main style={{ minWidth: 0 }}>
          {/* Mobile collapsible TOC */}
          {isMobile && (
            <div style={{ marginBottom: spacing.lg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
              <button
                onClick={() => setTocOpen(o => !o)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: `${spacing.sm} ${spacing.md}`,
                  background: colors.surface,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                <span>Contents</span>
                <span style={{ fontSize: '10px', color: colors.textMuted }}>{tocOpen ? '▲' : '▼'}</span>
              </button>
              {tocOpen && (
                <div style={{ background: colors.surface, borderTop: `1px solid ${colors.border}`, padding: `${spacing.sm} ${spacing.md}` }}>
                  {TOC.map(item => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={() => setTocOpen(false)}
                      style={{
                        display: 'block',
                        padding: `6px ${item.label.startsWith('  ') ? spacing.lg : spacing.sm}`,
                        fontSize: fontSize.sm,
                        color: activeSection === item.id ? colors.primary : colors.textSecondary,
                        textDecoration: 'none',
                        fontWeight: activeSection === item.id ? 600 : 400,
                      }}
                    >
                      {item.label.trim()}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ marginBottom: spacing.xl }}>
            <h1 style={{ fontSize: '2rem', fontWeight: 800, color: colors.text, margin: `0 0 ${spacing.sm}` }}>
              API Integration Guide
            </h1>
            <p style={{ fontSize: fontSize.lg, color: colors.textSecondary, margin: 0 }}>
              Generate documents from your application using the DocMorphia REST API.
            </p>
          </div>

          <Section id="overview" title="Overview">
            <P>
              DocMorphia exposes a JSON REST API under <Code>{BASE_URL}</Code>. All client-facing
              endpoints live under <Code>/client</Code> and authenticate via an API key you generate
              in your account settings.
            </P>
            <P>
              The general integration pattern is:
            </P>
            <ol style={{ color: colors.textSecondary, lineHeight: 2, paddingLeft: spacing.lg }}>
              <li>Create a template in the DocMorphia UI and upload your <Code>.docx</Code> file.</li>
              <li>Iterate in the <strong>Test</strong> workspace, rendering with a Test API key.</li>
              <li>Promote the version you are happy with to <strong>Live</strong>.</li>
              <li>Call <Code>POST /client/render</Code> with a Live API key, the <strong>Template ID</strong> and a JSON payload.</li>
              <li>Receive PDF bytes — save, serve, or stream them directly to users.</li>
            </ol>
            <Callout type="info">
              All endpoints use <strong>POST</strong> even for read operations. Request bodies are JSON
              unless noted. Responses are JSON or <Code>application/pdf</Code> bytes.
            </Callout>
            <P>
              Machine-readable: the <a href="/openapi.json" style={{ color: colors.primary }}>OpenAPI 3.1 spec</a> describes
              every client endpoint for code generators and coding agents, and{' '}
              <a href="/llms.txt" style={{ color: colors.primary }}>llms.txt</a> /{' '}
              <a href="/llms-full.txt" style={{ color: colors.primary }}>llms-full.txt</a> give LLMs the whole reference.
            </P>
          </Section>

          <Section id="auth" title="Authentication">
            <P>
              Client API requests authenticate with an <strong>API key</strong> passed in the{' '}
              <Code>X-Api-Key</Code> header.
            </P>
            <CodeBlock language="HTTP">{`POST /api/v1/client/render
X-Api-Key: live_xxxxxxxxxxxxxxxxxxxx
Content-Type: application/json

{ "templateId": "...", "payloadJson": "{}" }`}</CodeBlock>

            <SubSection id="get-api-key" title="Getting an API Key">
              <P>
                Sign in to DocMorphia, go to <strong>API Keys</strong> in the sidebar, and click{' '}
                <strong>Create API Key</strong>. Give it a descriptive name (e.g. "Production Server").
                The full key is shown <em>once</em> — copy it immediately and store it in your
                environment secrets (never in source code or client-side bundles).
              </P>
              <Callout type="warning">
                API keys reach your team's templates. Treat them like passwords. A key belongs to
                whichever workspace (Live/Test) was active when you created it — switch workspaces
                before creating one to choose. On Enhanced and above a key can also be narrowed to
                one folder.
              </Callout>
            </SubSection>

            <SubSection id="key-format" title="Key Format">
              <P>
                Keys look like <Code>live_&lt;random&gt;</Code> or <Code>test_&lt;random&gt;</Code> —
                the prefix reflects the workspace the key was created in. Pass the full string,
                including the prefix, in the <Code>X-Api-Key</Code> header.
              </P>
            </SubSection>

            <SubSection id="live-test" title="Live and Test keys">
              <P>
                Templates are shared by both workspaces; at most one version of each template is
                promoted to Live at a time. What a key renders depends on its workspace:
              </P>
              <ul style={{ color: colors.textSecondary, lineHeight: 2, paddingLeft: spacing.lg }}>
                <li><strong>Live key</strong> — only the promoted version. By <Code>templateId</Code> it renders the Live version; a <Code>templateVersionId</Code> that is not Live returns 403 <Code>VERSION_NOT_LIVE</Code>, and a template with nothing promoted returns 403 <Code>TEMPLATE_NOT_LIVE</Code>.</li>
                <li><strong>Test key</strong> — any version, watermarked. By <Code>templateId</Code> it renders the newest version, drafts included.</li>
              </ul>
              <Callout type="tip">
                Render by <Code>templateId</Code> with a Live key and your integration picks up each
                newly promoted version without a code change. Promotion can be immediate or scheduled.
              </Callout>
            </SubSection>
          </Section>

          <Section id="workflow" title="Core Workflow">
            <P>A complete server-side integration in Node.js / TypeScript:</P>
            <CodeBlock language="TypeScript">{`const BASE = 'https://api.docmorphia.com/api/v1'
const API_KEY = process.env.DOCMORPHIA_API_KEY!

// 1. Find your template ID (do this once, store in config)
const { items } = await fetch(\`\${BASE}/client/templates\`, {
  method: 'POST',
  headers: { 'X-Api-Key': API_KEY, 'Content-Type': 'application/json' },
  body: JSON.stringify({}),
}).then(r => r.json())

const template = items.find((t: any) => t.name === 'Invoice')
const templateId = template.templateId   // save this

// 2. Build your payload
const payload = JSON.stringify({
  invoiceNumber: 'INV-2024-001',
  clientName: 'Acme Corp',
  lineItems: [
    { description: 'Widget', qty: '10', unitPrice: '$5.00', amount: '$50.00' },
    { description: 'Gadget', qty:  '2', unitPrice: '$25.00', amount: '$50.00' },
  ],
  subtotal: '$100.00',
  tax:       '$8.00',
  total:    '$108.00',
})

// 3. Render — receive raw PDF bytes
const pdfResponse = await fetch(\`\${BASE}/client/render\`, {
  method: 'POST',
  headers: { 'X-Api-Key': API_KEY, 'Content-Type': 'application/json' },
  body: JSON.stringify({ templateId, payloadJson: payload }),
})

if (!pdfResponse.ok) throw new Error(await pdfResponse.text())

// 4. Stream to the browser
const pdfBytes = await pdfResponse.arrayBuffer()
res.setHeader('Content-Type', 'application/pdf')
res.setHeader('Content-Disposition', 'attachment; filename="invoice.pdf"')
res.send(Buffer.from(pdfBytes))`}</CodeBlock>
          </Section>

          <Section id="endpoints" title="API Reference">

            <SubSection id="render" title="Render to PDF">
              <EndpointCard
                method="POST"
                path="/client/render"
                description="Render a template with the supplied JSON payload. Pass templateId (Live key: the promoted version; Test key: the newest) or a specific templateVersionId. Returns the document bytes."
                request={`{
  "templateId": "3fa85f64-...",         // or "templateVersionId"
  "payloadJson": "{\"name\":\"Alice\"}", // JSON-encoded string
  "outputFormat": "pdf",                // optional: pdf (default), docx, xlsx, pptx
  "locale": "fr-CA"                     // optional: render language (BCP-47). See Localization below.
}`}
                response={`// HTTP 200 — Content-Type: application/pdf (or the requested format)
// Binary document bytes

// HTTP 400 — locale is not a recognized language tag
// HTTP 403 — TEMPLATE_NOT_LIVE / VERSION_NOT_LIVE (Live key)
// HTTP 403 — template belongs to another team or outside the key's folder
// HTTP 404 — version not found
// HTTP 500 — render failed
{ "error": "...", "renderJobId": "..." }`}
              />
              <Callout type="info">
                <strong>Localization.</strong> <code>locale</code> picks the template's translations (fr-CA, then fr, then the
                default text) and the culture for date, number and currency formatters. Without it, a localized template reads a
                top-level <code>locale</code> key from the payload, then falls back to its default language. Every render endpoint
                accepts it (<code>/render/generate</code>, <code>preview</code>, <code>submit</code>, <code>batch</code>,
                <code>inline</code>, …). Strings with no translation are listed in the render job's warnings. Translations are
                managed per version under <code>/translations</code> (<code>list</code>, <code>get</code>, <code>save</code>,
                <code>import</code>, <code>export</code>, <code>accept-drafts</code>); each write saves a new
                draft version. Translate with AI (Ultimate, paid per use): <code>ai-quote</code> lists the languages and prices,
                and with <code>locales</code> issues a single-use fixed-price quote; <code>ai-draft</code> runs it with the
                <code>quoteId</code> and the same <code>locales</code>, and charges at most the quote, on the next invoice.
              </Callout>
              <Callout type="info">
                <strong>PDF passwords.</strong> <code>/render/generate</code> and <code>/render/submit</code> accept
                <code>pdfUserPassword</code> (required to open the PDF) and <code>pdfOwnerPassword</code>, PDF output only, at most
                32 characters each. They are stored only encrypted, so <code>/render/rerender</code> can reproduce the protected
                document, and are never returned by any job endpoint. There are no permission options: every permission (print,
                copy, edit) is granted, so an owner password without a user password protects nothing — the file opens freely.
              </Callout>
              <Callout type="tip">
                <Code>payloadJson</Code> is a <em>JSON-encoded string</em> (a string containing JSON),
                not a nested object. Stringify your payload object before embedding it in the request body.
              </Callout>
            </SubSection>

            <SubSection id="list" title="List Templates">
              <EndpointCard
                method="POST"
                path="/client/templates"
                description="List the team's templates, optionally filtered by folder. A Live key sees only templates with a version promoted to Live."
                request={`{
  "folderId": null   // optional — omit or null for all templates
}`}
                response={`[
  {
    "templateId": "3fa85f64-...",
    "name": "Invoice",
    "description": "Monthly invoice template",
    "folderId": null,
    "currentVersionId": "9b1deb4d-...",
    "liveVersionId": "9b1deb4d-...",   // null when nothing is promoted
    "createdAt": "2024-01-15T10:30:00Z"
  }
]`}
              />
            </SubSection>

            <SubSection id="variables" title="Get Template Variables">
              <EndpointCard
                method="POST"
                path="/client/templates/version-variables"
                description="List the variables declared in a template version. Use this to discover what fields to include in your payload."
                request={`{
  "templateVersionId": "9b1deb4d-..."
}`}
                response={`[
  {
    "normalizedKey": "invoicenumber",
    "rawName": "invoiceNumber",
    "variableKindLookupId": 10,   // 10=scalar, 20=loop, 30=conditional
    "occurrenceCount": 1,
    "contextSnippet": "{{invoiceNumber}}"
  },
  {
    "normalizedKey": "lineitems",
    "rawName": "lineItems",
    "variableKindLookupId": 20,
    "occurrenceCount": 1,
    "contextSnippet": "{{#each lineItems}}"
  }
]`}
              />
              <P>
                <strong>Variable kinds:</strong>
              </P>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: spacing.md }}>
                <thead>
                  <tr style={{ background: colors.background }}>
                    {['ID', 'Kind', 'Description'].map(h => (
                      <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['10', 'Scalar', 'Simple string value (or nested object path)'],
                    ['20', 'Loop', 'Array key — drives a repeating section'],
                    ['30', 'Conditional', 'Boolean key — controls an #if/#unless block'],
                  ].map(([id, kind, desc]) => (
                    <tr key={id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: 'monospace', color: colors.text }}>{id}</td>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontWeight: 600, color: colors.text }}>{kind}</td>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, color: colors.textSecondary }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SubSection>

            <SubSection id="embed-token" title="Embed Token">
              <EndpointCard
                method="POST"
                path="/client/embed-token"
                description="Exchange your long-lived API key for a short-lived (1-hour) JWT. Pass this token to your frontend so browsers can call /client/render without exposing the API key. Requires API key auth (not a previously issued embed token)."
                request={`{
  "templateId": "3fa85f64-..."   // optional — scope token to one template
}`}
                response={`{
  "token": "eyJhbGci...",
  "expiresAt": "2024-01-15T11:30:00Z"
}`}
              />
            </SubSection>
          </Section>

          <Section id="iframe" title="iframe Embedding">
            <P>
              To display a rendered PDF inside an <Code>&lt;iframe&gt;</Code> or{' '}
              <Code>&lt;embed&gt;</Code> tag on your page, use the embed-token pattern:
              your server holds the API key; the browser uses the short-lived token.
            </P>

            <SubSection id="iframe-server" title="1 — Server: generate the embed token">
              <CodeBlock language="Node.js / Express">{`app.get('/invoice/:id/embed', async (req, res) => {
  // Fetch the embed token from your server
  const { token } = await fetch(
    'https://api.docmorphia.com/api/v1/client/embed-token',
    {
      method: 'POST',
      headers: { 'X-Api-Key': process.env.DOCMORPHIA_API_KEY!, 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateId: INVOICE_TEMPLATE_ID }),
    }
  ).then(r => r.json())

  // Pass the token + payload to your frontend page
  res.render('invoice', { embedToken: token, invoiceId: req.params.id })
})`}</CodeBlock>
            </SubSection>

            <SubSection id="iframe-browser" title="2 — Browser: fetch PDF and create Blob URL">
              <CodeBlock language="JavaScript (browser)">{`async function loadPdfInIframe(embedToken, templateVersionId, payload) {
  const response = await fetch(
    'https://api.docmorphia.com/api/v1/client/render',
    {
      method: 'POST',
      headers: {
        Authorization: \`Bearer \${embedToken}\`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        templateVersionId,
        payloadJson: JSON.stringify(payload),
      }),
    }
  )

  if (!response.ok) throw new Error('Render failed')

  const pdfBlob = await response.blob()
  const blobUrl = URL.createObjectURL(pdfBlob)

  const iframe = document.getElementById('pdf-frame')
  iframe.src = blobUrl

  // Clean up the object URL when the iframe is removed
  iframe.addEventListener('load', () => {
    // optional: revoke after a delay to let the browser cache it
  }, { once: true })
}

// Call after DOMContentLoaded:
loadPdfInIframe(
  window.__EMBED_TOKEN__,       // injected by your server
  window.__TEMPLATE_VERSION_ID__,
  { invoiceNumber: 'INV-001', clientName: 'Acme' }
)`}</CodeBlock>
              <CodeBlock language="HTML">{`<iframe
  id="pdf-frame"
  style="width: 100%; height: 800px; border: none;"
  title="Invoice PDF"
></iframe>`}</CodeBlock>
            </SubSection>

            <SubSection id="iframe-react" title="React component example">
              <CodeBlock language="React / TypeScript">{`import { useEffect, useRef } from 'react'

interface PdfFrameProps {
  embedToken: string
  templateVersionId: string
  payload: Record<string, unknown>
}

export function PdfFrame({ embedToken, templateVersionId, payload }: PdfFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const blobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function render() {
      const res = await fetch(
        'https://api.docmorphia.com/api/v1/client/render',
        {
          method: 'POST',
          headers: {
            Authorization: \`Bearer \${embedToken}\`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            templateVersionId,
            payloadJson: JSON.stringify(payload),
          }),
        }
      )
      if (!res.ok || cancelled) return
      const blob = await res.blob()
      if (cancelled) return
      const url = URL.createObjectURL(blob)
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = url
      if (iframeRef.current) iframeRef.current.src = url
    }

    render()
    return () => {
      cancelled = true
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
    }
  }, [embedToken, templateVersionId, JSON.stringify(payload)])

  return (
    <iframe
      ref={iframeRef}
      style={{ width: '100%', height: '800px', border: 'none' }}
      title="Document Preview"
    />
  )
}`}</CodeBlock>
              <Callout type="tip">
                Embed tokens expire after 1 hour. If you need long-lived browser sessions, refresh
                the token server-side and update the component's prop. You can optionally scope the
                token to a specific template ID to prevent it being used for unintended templates.
              </Callout>
            </SubSection>
          </Section>

          <Section id="mcp" title="MCP Server">
            <P>
              The DocMorphia MCP server lets AI assistants (Claude Desktop, Claude Code, Cursor, VS Code, etc.)
              call your templates directly — no REST boilerplate required. Customers install it with a single{' '}
              <Code>npx</Code> command and authenticate with their existing API key.
            </P>

            <SubSection id="mcp-install" title="Install">
              <P>Add the following block to your MCP client config and replace the placeholder with your API key.</P>
              <P>
                <strong>Claude Desktop</strong> — <Code>~/Library/Application Support/Claude/claude_desktop_config.json</Code> (macOS)
                or <Code>%APPDATA%\Claude\claude_desktop_config.json</Code> (Windows):
              </P>
              <CodeBlock language="json">{`{
  "mcpServers": {
    "docmorphia": {
      "command": "npx",
      "args": ["-y", "@docmorphia/mcp-server"],
      "env": {
        "DOCMORPHIA_API_KEY": "live_xxxxxxxxxxxx"
      }
    }
  }
}`}</CodeBlock>
              <P>
                <strong>Cursor</strong> — same block in <Code>~/.cursor/mcp.json</Code>.{' '}
                <strong>VS Code</strong> — same block in <Code>.vscode/mcp.json</Code> or user settings under <Code>mcp.servers</Code>.
              </P>
              <Callout type="tip">
                Set <Code>DOCMORPHIA_API_BASE_URL</Code> in <Code>env</Code> to override the API base URL (useful for
                dev/staging environments).
              </Callout>
            </SubSection>

            <SubSection id="mcp-tools" title="Available tools">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.background }}>
                    {['Tool', 'Description'].map(h => (
                      <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['docmorphia_list_templates', 'List templates. Returns names, currentVersionId and liveVersionId values.'],
                    ['docmorphia_get_template_variables', 'Return all {{variables}} for a template version, with type (scalar/loop/conditional) and normalizedKey for building the payload.'],
                    ['docmorphia_render_document', 'Render a template to PDF and save to a local file. Returns the file path.'],
                    ['docmorphia_create_embed_token', 'Exchange an API key for a short-lived JWT for browser iframe embedding.'],
                  ].map(([tool, desc]) => (
                    <tr key={tool} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: 'monospace', fontSize: '13px', color: colors.primary, whiteSpace: 'nowrap' }}>{tool}</td>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, color: colors.textSecondary, fontSize: fontSize.sm }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SubSection>

            <SubSection id="mcp-example" title="Example session">
              <CodeBlock language="text">{`You: List my templates
Assistant: Found 3 templates — Invoice, Offer Letter, NDA.

You: What variables does the Invoice template need?
Assistant: clientName, invoiceNumber, issueDate (scalars) and a
repeating lineItems section with description, qty, unitPrice, amount.

You: Render an invoice for Acme Corp, #1001, today, with one
line item: "Consulting" qty 5 at $200.
Assistant: Rendered to /var/folders/.../docmorphia-abc1-1.pdf`}</CodeBlock>
            </SubSection>

            <SubSection id="mcp-verify" title="Verify the install">
              <CodeBlock language="bash">{`DOCMORPHIA_API_KEY=live_... \\
  npx @modelcontextprotocol/inspector npx @docmorphia/mcp-server`}</CodeBlock>
              <P>Opens the MCP Inspector UI where you can call each tool interactively before wiring it into an assistant.</P>
            </SubSection>
          </Section>

          <Section id="docmosis" title="Migrating from Docmosis">
            <P>
              Applications written against the Docmosis Cloud (DWS3 / DWS4) or Tornado web services can call DocMorphia
              unchanged. Import your templates (Templates → Import, which converts Docmosis syntax), create an API key,
              then change two things in your client: the base URL and the access key.
            </P>
            <CodeBlock language="bash">{`# Before: https://us1.dws4.docmosis.com/api/render
curl -X POST ${DOCMOSIS_BASE_URL}/render \
  -H "Content-Type: application/json" \
  -d '{
    "accessKey": "live_...",
    "templateName": "Billing/Invoice.docx",
    "outputName": "invoice.pdf",
    "data": { "customer": "Acme", "total": 1250 }
  }' --output invoice.pdf`}</CodeBlock>
            <P>
              The access key is a DocMorphia API key, sent as <Code>accessKey</Code> in the body or as an
              {' '}<Code>accessKey</Code> header. A Live key renders the version promoted to Live; a Test key renders the
              newest version with a test watermark. <Code>templateName</Code> is a path of folder names ending in the
              template name, matched without regard to case and with or without the file extension; a folder-scoped key
              reaches only its own folder. If two templates match the same path the request is refused, so rename or move
              one. Requests may be JSON, form-urlencoded or multipart, and <Code>/renderForm</Code> takes data from the form
              fields.
            </P>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: spacing.md }}>
              <thead>
                <tr style={{ background: colors.background }}>
                  {['Parameters', 'Support'].map(h => (
                    <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['accessKey, templateName, outputName, outputFormat, data, strictParams, ignoreUnknownParams', 'Supported. Formats: pdf, docx, odt, html, txt. Several templates with one PDF outputName are merged into one PDF; several formats, several outputNames or compressSingleFormat return a zip.'],
                  ['storeTo', 'stream (default) and mailto:address, with optional per-destination formats (stream:pdf,docx). Up to 5 mailto destinations.'],
                  ['streamResultInResponse, requestId, passwordProtect, pdfWatermark', 'Supported. passwordProtect applies to PDF output only.'],
                  ['Image values', 'image:base64:…, data:image/… and [imageUrl:https://…] (public addresses only).'],
                  ['devMode, sourceId, tags, pdfVersion 1.x', 'Accepted; no effect.'],
                  ['stylesInText, pdfTagged, pdfRestrict*, pdfWatermark styling, mail subject and body', 'Ignored; named back in the X-DocMorphia-Ignored-Params response header.'],
                  ['XML data, rtf/doc output, PDF/A, e-invoices, storeTo s3, [userImage:…]', 'Not supported: answered with a 400.'],
                ].map(([params, support]) => (
                  <tr key={params} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: 'monospace', fontSize: fontSize.sm, color: colors.text }}>{params}</td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, color: colors.textSecondary }}>{support}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <P>
              Also available: <Code>ping</Code>, <Code>status</Code>, <Code>environment/ready</Code>,
              {' '}<Code>environment/summary</Code>, <Code>accountReady</Code> and <Code>accountSummary</Code>.
            </P>
            <P>
              Template services: <Code>listTemplates</Code>, <Code>getTemplateDetails</Code>, <Code>getTemplateStructure</Code> and
              {' '}<Code>getSampleData</Code> (JSON) work with any key, within its folder and workspace (a Live key sees only templates
              in Live). <Code>uploadTemplate</Code>, <Code>getTemplate</Code> and <Code>deleteTemplate</Code> need a Test key created with
              {' '}<em>Also manage templates</em>. An upload (Word .docx) is converted from Docmosis syntax into a new version in Test,
              creating any folders in its name; promote it to Live in DocMorphia. A template with a version in Live cannot be deleted
              here. The image store, batch upload, <Code>convert</Code> and DWS3 file storage are not available.
            </P>
            <P>
              Errors keep the familiar shape, <Code>{'{"succeeded": false, "shortMsg": "…", "longMsg": "…", "requestId": "…"}'}</Code>.
              Bad requests and render errors are 400, a missing or invalid key is 401, a billing problem is 402, and 429 / 503
              (render capacity) carry Retry-After. Successful renders return X-Docmosis-PagesRendered and
              X-Docmosis-RequestId; X-Docmosis-Server identifies the service as <Code>docmorphia</Code>.
            </P>
            <Callout type="info">
              Each template and format in a request is one render and is billed as one.
            </Callout>
          </Section>

          <Section id="carbone" title="Migrating from Carbone">
            <P>
              Applications written against the Carbone Cloud HTTP API, or one of its SDKs, can call DocMorphia's
              Carbone-compatible API by changing the base URL and the API key. Templates keep their Carbone tags: each
              upload is converted when it is stored.
            </P>
            <CodeBlock language="bash">{`# Before: https://api.carbone.io
curl -X POST ${CARBONE_BASE_URL}/template \
  -H "Authorization: Bearer test_..." \
  -F payload= -F template=@invoice.docx
# {"success":true,"data":{"templateId":"<sha256>","templateExtension":"docx"}}

curl -X POST "${CARBONE_BASE_URL}/render/<templateId>?download=true" \
  -H "Authorization: Bearer test_..." -H "Content-Type: application/json" \
  -d '{ "data": { "customer": "Acme", "total": 1250 }, "convertTo": "pdf" }' --output invoice.pdf`}</CodeBlock>
            <P>
              The key is a DocMorphia API key, sent as <Code>Authorization: Bearer</Code>. A template id is the SHA-256 of
              the uploaded file, as in Carbone, so an SDK that hashes the file and uploads it on a 404 works as is. An id
              is only ever known to the team that uploaded the file. Uploading, downloading and deleting templates need a
              Test key created with <em>Also manage templates</em>; an upload becomes a template in Test, which you
              promote to Live in DocMorphia. A Test key renders with a test watermark. A Live key is read-only: it
              renders a template by its id once that template is in Live.
            </P>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: spacing.md }}>
              <thead>
                <tr style={{ background: colors.background }}>
                  {['Endpoint or option', 'Support'].map(h => (
                    <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['POST /template, GET /template/:id, DELETE /template/:id', 'Supported for Word (.docx) templates, multipart or JSON base64. A template in Live cannot be deleted here.'],
                  ['POST /render/:id, POST /render/template, GET /render/:renderId, GET /status', 'Supported. ?download=true returns the file directly. A renderId downloads once, without a key, within an hour.'],
                  ['data, convertTo, reportName, lang, complement, enum, translations', 'Supported. Formats: pdf, docx, odt, html, txt, xlsx, ods, csv, pptx, odp.'],
                  ['convertTo.formatOptions', 'EncryptFile with DocumentOpenPassword, PermissionPassword and Watermark are applied (PDF). Other options are ignored and named in the X-DocMorphia-Ignored-Params header.'],
                  ['converter, hardRefresh, timezone, variableStr, failOn', 'Accepted; no effect. Named in X-DocMorphia-Ignored-Params.'],
                  ['carbone-webhook-url, carbone-webhook-header-*', 'Supported on POST /render/:id (Async rendering plan feature). The render is queued and answered at once; when it is ready, {"success": true, "data": {"renderId": "…"}} is POSTed to the URL with the forwarded headers (one retry). The URL must be public http(s): private and internal addresses are refused.'],
                  ['Template versions (carbone-version: 5): versioning, id, name, comment, category, tags, deployedAt', 'Supported. versioning=true gives the upload a Template ID; id adds a version to it. A Template ID renders its most recently deployed version (the newest when none is) with a Test key, and the version promoted to Live with a Live key. A versionId renders exactly that version.'],
                  ['GET /templates, GET /templates/categories, GET /templates/tags, PATCH /template/:id', 'Supported, with the id, versionId, category, origin, includeVersions, search, limit and cursor filters. Moving a version to another Template ID is not supported, and templates do not expire (expireAt must be 0).'],
                  ['Batch rendering, currency conversion, PDF/A, template samples', 'Not supported yet: answered with a Carbone-style error naming the option.'],
                ].map(([params, support]) => (
                  <tr key={params} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: 'monospace', fontSize: fontSize.sm, color: colors.text }}>{params}</td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, color: colors.textSecondary }}>{support}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <P>
              Errors keep Carbone&apos;s shape, <Code>{'{"success": false, "error": "…", "code": "w100"}'}</Code>, with its status
              codes (404 for an unknown template or render, 401 for a missing or invalid key). A billing problem is 402, and
              429 / 503 (render capacity) carry Retry-After.
            </P>
            <Callout type="info">
              Carbone is a trademark of CarboneIO SAS. DocMorphia is not affiliated with or endorsed by CarboneIO; the
              name is used only to say which API this is compatible with.
            </Callout>
          </Section>

          <Section id="errors" title="Error Handling">
            <P>All error responses use standard HTTP status codes with a JSON body:</P>
            <CodeBlock language="JSON">{`{
  "message": "Template version not found.",
  "status": 404
}`}</CodeBlock>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.background }}>
                  {['Status', 'Meaning', 'Common causes'].map(h => (
                    <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['401', 'Unauthorized', 'Missing or invalid X-Api-Key header'],
                  ['403', 'Forbidden', 'Template belongs to a different team or outside the key\'s folder; TEMPLATE_NOT_LIVE / VERSION_NOT_LIVE with a Live key; PLAN_UPGRADE_REQUIRED'],
                  ['402', 'Payment Required', 'No usable payment method on file'],
                  ['404', 'Not Found', 'Template version ID does not exist'],
                  ['422', 'Unprocessable', 'Template has moniker normalization conflicts (on upload)'],
                  ['429', 'Too Many Requests', 'TEAM_CONCURRENCY_LIMIT: your team already has its plan\'s maximum of documents rendering at once (Standard 8, Enhanced 16, Ultimate 32, across all servers); QUOTA_EXCEEDED: request rate limit. Honour Retry-After (seconds). Successful renders carry X-Concurrency-Limit / X-Concurrency-Remaining.'],
                  ['503', 'Service Unavailable', 'SERVER_BUSY: the server\'s render capacity and wait queue are full. Retry after the Retry-After seconds.'],
                  ['500', 'Server Error', 'Render engine failure — check errorMessage in body'],
                ].map(([code, meaning, cause]) => (
                  <tr key={code} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: 'monospace', fontWeight: 600, color: code.startsWith('4') ? colors.warning : colors.error }}>{code}</td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontWeight: 500, color: colors.text }}>{meaning}</td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, color: colors.textSecondary }}>{cause}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* Footer */}
          <div style={{
            marginTop: spacing.xxl,
            paddingTop: spacing.lg,
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
              DocMorphia API v1
            </span>
            <a
              href="/docs/templates"
              style={{ fontSize: fontSize.sm, color: colors.primary, textDecoration: 'none', fontWeight: 500 }}
            >
              Template Writing Guide →
            </a>
          </div>
        </main>
      </div>
  )
}
